import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Dialog as SheetPrimitive } from 'radix-ui';
import { cn } from '@worldcoo/ui/lib/utils';
import { observeSnapScroller, settledDismissed, snapProgress } from '@/lib/snap-scroller';

/**
 * The DISMISS GESTURE IS A NATIVE SCROLL to the empty slot of a two-slot scroll-snap container, so drag
 * and fling run on the compositor. Not `vaul` (~10.4KB brotli, main-thread pointer handlers): Radix's
 * Dialog is already on the first-paint chunk.
 */

const SheetTitle = SheetPrimitive.Title;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
	return <SheetPrimitive.Root data-slot="snap-sheet" {...props} />;
}

/**
 * The backdrop and the panel must be the Portal's OWN children: each gets a `Presence`, which measures
 * the exit on its child's DOM node, and a fragment-returning wrapper has none, so the close never animates.
 */
function SheetContent({
	className,
	panelClassName,
	children,
	onDismiss,
	...contentProps
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
	onDismiss: () => void;
	/** Paint here, never the scroller: its background would fill the empty slot a drag reveals. */
	panelClassName?: string;
}) {
	// In STATE, not a ref: an effect keyed on a ref object never re-runs when the node arrives.
	const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
	const backdropRef = useRef<HTMLDivElement>(null);
	/** Dismissed BY THE FINGER: dropping the exit animation stops `fade-out-0` flashing the full dim back. */
	const [dragDismissed, setDragDismissed] = useState(false);
	// This component outlives a close, so a stale flag would strip the exit animation off every later close.
	const attachScroller = useCallback((node: HTMLDivElement | null) => {
		setScroller(node);
		if (node) setDragDismissed(false);
	}, []);

	// Before the first paint: a fresh scroller starts at 0, the dismissed slot.
	useLayoutEffect(() => {
		if (!scroller) return;
		scroller.scrollLeft = scroller.scrollWidth - scroller.clientWidth;
	}, [scroller]);

	useLayoutEffect(() => {
		if (!scroller) return;

		const maxScroll = () => scroller.scrollWidth - scroller.clientWidth;
		const progress = () => snapProgress(scroller.scrollLeft, maxScroll());
		// A resize mid-gesture leaves it a pixel off, and then the next drag never travels its full distance.
		const repark = () => {
			const rest = maxScroll();
			if (Math.abs(scroller.scrollLeft - rest) > 1) scroller.scrollLeft = rest;
		};
		let scrolling = false;

		const gesture = observeSnapScroller(scroller, {
			onStart: () => {
				scrolling = true;
			},
			// Written onto the node, so the backdrop TRACKS the finger rather than chasing it through a transition.
			onFrame: () => {
				const backdrop = backdropRef.current;
				if (backdrop) backdrop.style.opacity = (1 - progress()).toFixed(3);
			},
			onSettle: () => {
				scrolling = false;
				if (settledDismissed(progress())) {
					setDragDismissed(true);
					onDismiss();
					return;
				}
				repark();
				const backdrop = backdropRef.current;
				if (backdrop) backdrop.style.removeProperty('opacity');
			},
		});

		// Never while a scroll is in flight: repositioning under a fling eats the gesture.
		const observer = new ResizeObserver(() => {
			if (scrolling) return;
			repark();
		});
		observer.observe(scroller);

		return () => {
			gesture.stop();
			observer.disconnect();
		};
	}, [scroller, onDismiss]);

	return (
		<SheetPrimitive.Portal>
			<SheetPrimitive.Overlay
				ref={backdropRef}
				data-slot="snap-sheet-backdrop"
				className={cn(
					'bg-black/10 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 z-40 duration-200',
					dragDismissed
						? 'animate-none'
						: 'data-open:animate-in data-closed:animate-out data-open:fade-in-0 data-closed:fade-out-0',
					// `!`: a bare `motion-reduce:animate-none` loses to `data-open:` on source order.
					'motion-reduce:animate-none!',
				)}
			/>
			<SheetPrimitive.Content
				// Radix sets `aria-hidden` on the background but no `aria-modal`, and the APG asks for both.
				aria-modal
				data-slot="snap-sheet-content"
				className={cn(
					// `overscroll-x-none` is load-bearing: iOS takes a chained-out drag as the back-swipe.
					'fixed inset-0 z-50 flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-none outline-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					'duration-300',
					dragDismissed
						? 'animate-none'
						: 'data-open:animate-in data-closed:animate-out data-open:slide-in-from-right data-closed:slide-out-to-right',
					'motion-reduce:animate-none!',
					className,
				)}
				{...contentProps}
				// AFTER the spread: in React 19 the rest-spread carries an `undefined` `ref` that overwrites this.
				ref={attachScroller}
			>
				{/* What the panel scrolls away INTO; never a tap target, as the panel covers the viewport. */}
				<div aria-hidden data-slot="snap-sheet-dismiss-area" className="h-full w-full shrink-0 snap-start" />
				{/* The gesture is horizontal, so the panel's vertical scroll must not chain anywhere. */}
				<div
					data-slot="snap-sheet-panel"
					// `relative`, or decoration positions against the SCROLLER and lands on the empty slot.
					className={cn(
						'relative flex h-full w-full shrink-0 snap-end flex-col overflow-y-auto overscroll-y-contain',
						panelClassName,
					)}
				>
					{children}
				</div>
			</SheetPrimitive.Content>
		</SheetPrimitive.Portal>
	);
}

export { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger };
